import { inject, Injectable, signal } from '@angular/core';
import {
  catchError,
  defaultIfEmpty,
  filter,
  from,
  map,
  mergeMap,
  Observable,
  of,
  switchMap,
  take,
  tap,
  toArray,
} from 'rxjs';
import type { RequestModel } from '@core/models';
import type { RequestFileCategory } from '@core/types';
import { RequestFilesService } from '@core/services/request/request-files.service';
import type {
  GroupUploadFile,
  GroupUploadJob,
  GroupUploadResult,
} from './group-files-upload.types';

const GROUP_UPLOAD_CONCURRENCY = 10;
const AUTOGENERATED_OWNER_IDS = new Set([222, 679]);
const AUTOGENERATED_CATEGORY_PREFIXES = new Set([
  'obrz_app_k_dl',
  'obrz_app_k_dkp',
  'obrz_app_pts_psm',
  'obrz_rekvizit',
]);

@Injectable()
export class GroupFilesUploadManagerService {
  private readonly requestFilesService = inject(RequestFilesService);

  private readonly _$total = signal(0);
  private readonly _$completed = signal(0);
  private readonly _$results = signal<GroupUploadResult[]>([]);

  readonly $total = this._$total.asReadonly();
  readonly $completed = this._$completed.asReadonly();
  readonly $results = this._$results.asReadonly();

  getEligibleRequests(
    category: RequestFileCategory,
    requests: RequestModel[],
  ): RequestModel[] {
    if (!AUTOGENERATED_CATEGORY_PREFIXES.has(category.fileRenamePrefix)) {
      return requests;
    }

    return requests.filter(
      request => !AUTOGENERATED_OWNER_IDS.has(request.counterpartyOwnerId?.id),
    );
  }

  distributeFiles(
    files: GroupUploadFile[],
    requests: RequestModel[],
  ): GroupUploadFile[] {
    return files.map(stagedFile => {
      const stem = this.getFilenameStem(stagedFile.file.name).toLowerCase();
      const matches = requests.filter(request =>
        [request.requestNumber, request.carVin, request.contract]
          .filter((value): value is string => Boolean(value))
          .some(value => value.toLowerCase() === stem),
      );

      if (matches.length === 1) {
        return {
          ...stagedFile,
          targetRequestIds: [matches[0].id],
          distribution: 'matched',
          ambiguousRequestIds: [],
        };
      }

      if (matches.length > 1) {
        return {
          ...stagedFile,
          targetRequestIds: [],
          distribution: 'ambiguous',
          ambiguousRequestIds: matches.map(request => request.id),
        };
      }

      return {
        ...stagedFile,
        distribution: 'none',
        ambiguousRequestIds: [],
      };
    });
  }

  upload$(
    category: RequestFileCategory,
    files: GroupUploadFile[],
    requests: RequestModel[],
  ): Observable<GroupUploadResult[]> {
    const jobs = this.createJobs(files, requests);

    this._$total.set(jobs.length);
    this._$completed.set(0);
    this._$results.set([]);

    if (!jobs.length) {
      return of([]);
    }

    return from(jobs).pipe(
      mergeMap(job => this.uploadJob$(category, job), GROUP_UPLOAD_CONCURRENCY),
      tap(result => {
        this._$completed.update(value => value + 1);
        this._$results.update(results => [...results, result]);
      }),
      toArray(),
      tap(results => this.notifyMainFilesUpdated(category, results)),
    );
  }

  retryFailed$(
    category: RequestFileCategory,
    files: GroupUploadFile[],
    requests: RequestModel[],
    failedResults: GroupUploadResult[],
  ): Observable<GroupUploadResult[]> {
    const fileById = new Map(files.map(file => [file.id, file]));
    const requestById = new Map(requests.map(request => [request.id, request]));
    const retries = failedResults
      .filter(result => result.status !== 'success')
      .map(result => ({
        result,
        stagedFile: fileById.get(result.fileId),
        request: requestById.get(result.requestId),
      }))
      .filter(
        (
          retry,
        ): retry is {
          result: GroupUploadResult;
          stagedFile: GroupUploadFile;
          request: RequestModel;
        } => Boolean(retry.stagedFile && retry.request),
      );

    this._$total.set(retries.length);
    this._$completed.set(0);
    this._$results.set([]);

    if (!retries.length) {
      return of([]);
    }

    return from(retries).pipe(
      mergeMap(
        retry =>
          retry.result.status === 'binary-error'
            ? this.uploadJob$(category, retry)
            : this.retryMetadata$(category, retry),
        GROUP_UPLOAD_CONCURRENCY,
      ),
      tap(result => {
        this._$completed.update(value => value + 1);
        this._$results.update(results => [...results, result]);
      }),
      toArray(),
      tap(results => this.notifyMainFilesUpdated(category, results)),
    );
  }

  private notifyMainFilesUpdated(
    category: RequestFileCategory,
    results: GroupUploadResult[],
  ): void {
    if (
      results.some(result => result.status === 'success') &&
      this.requestFilesService.mainRequestFilesRenamePrefixes.includes(
        category.fileRenamePrefix,
      )
    ) {
      this.requestFilesService.requestsMainFilesWereUpdated$.next();
    }
  }

  private createJobs(
    files: GroupUploadFile[],
    requests: RequestModel[],
  ): GroupUploadJob[] {
    const requestById = new Map(requests.map(request => [request.id, request]));

    return files.flatMap(stagedFile =>
      stagedFile.targetRequestIds
        .map(requestId => requestById.get(requestId))
        .filter((request): request is RequestModel => Boolean(request))
        .map(request => ({ stagedFile, request })),
    );
  }

  private uploadJob$(
    category: RequestFileCategory,
    job: GroupUploadJob,
  ): Observable<GroupUploadResult> {
    const { stagedFile, request } = job;

    return this.requestFilesService
      .uploadRequestFile$(
        request.id,
        request.requestNumber,
        category,
        stagedFile.file,
      )
      .pipe(
        filter(
          (
            progress,
          ): progress is typeof progress & {
            response: string;
          } => Boolean(progress.response),
        ),
        take(1),
        map(({ response }) => response),
        defaultIfEmpty(null),
        switchMap(response => {
          if (!response) {
            return of({
              fileId: stagedFile.id,
              requestId: request.id,
              status: 'binary-error' as const,
              error: new Error('Загрузка файла завершилась без имени файла'),
            });
          }

          return this.requestFilesService
            .saveRequestFileName$(
              request.id,
              category.id,
              response,
              stagedFile.file.name,
            )
            .pipe(
              map((): GroupUploadResult => ({
                fileId: stagedFile.id,
                requestId: request.id,
                status: 'success',
                uploadedFilename: response,
              })),
              catchError(error =>
                of({
                  fileId: stagedFile.id,
                  requestId: request.id,
                  status: 'metadata-error' as const,
                  uploadedFilename: response,
                  error,
                }),
              ),
            );
        }),
        catchError(error =>
          of({
            fileId: stagedFile.id,
            requestId: request.id,
            status: 'binary-error' as const,
            error,
          }),
        ),
      );
  }

  private retryMetadata$(
    category: RequestFileCategory,
    retry: GroupUploadJob & { result: GroupUploadResult },
  ): Observable<GroupUploadResult> {
    const { request, stagedFile, result } = retry;
    const uploadedFilename = result.uploadedFilename;

    if (!uploadedFilename) {
      return of(result);
    }

    return this.requestFilesService.source$(request.id).pipe(
      take(1),
      switchMap(files => {
        const alreadySaved = files.some(
          file =>
            file.filename === uploadedFilename ||
            file.originFilename === stagedFile.file.name,
        );

        if (alreadySaved) {
          return of({
            fileId: stagedFile.id,
            requestId: request.id,
            status: 'success' as const,
            uploadedFilename,
          });
        }

        return this.requestFilesService
          .saveRequestFileName$(
            request.id,
            category.id,
            uploadedFilename,
            stagedFile.file.name,
          )
          .pipe(
            map((): GroupUploadResult => ({
              fileId: stagedFile.id,
              requestId: request.id,
              status: 'success',
              uploadedFilename,
            })),
          );
      }),
      catchError(error =>
        of({
          fileId: stagedFile.id,
          requestId: request.id,
          status: 'metadata-error' as const,
          uploadedFilename,
          error,
        }),
      ),
    );
  }

  private getFilenameStem(filename: string): string {
    const lastDotIndex = filename.lastIndexOf('.');

    return lastDotIndex > 0 ? filename.slice(0, lastDotIndex) : filename;
  }
}
